import React from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { TypedMemo } from '../../../common/types';
import type { OutreachLiveWarmup } from '../outreachTypes';
import { RampBars } from './RampBars';
import { warmupDisplayDay } from './warmupDay';
import { campaignPageMessages as messages } from './campaignPageMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the campaign page; each part uses its own slice of it */
import shared from '../Outreach.module.scss';
import styles from './Campaign.module.scss';
/* eslint-enable css-modules/no-unused-class */

/**
 * The mailbox of the campaign (design outreach-v2 g1 side): "Warming up", the 21-day ramp and
 * whether the campaign's rate fits the mailbox today.
 */
const MailboxRampComponent: React.FC<{ warmup: OutreachLiveWarmup | null; rate: number | null }> = ({
  warmup,
  rate,
}) => {
  const intl = useIntl();
  const line = () => {
    if (warmup === null) {
      return intl.formatMessage(messages.mailboxNone);
    }
    if (!warmup.warming) {
      return intl.formatMessage(messages.mailboxWarm, { mailbox: warmup.mailboxAddress });
    }
    const head = intl.formatMessage(messages.mailboxLine, {
      mailbox: warmup.mailboxAddress,
      day: warmupDisplayDay(warmup),
      total: warmup.totalDays,
    });
    if (rate === null) {
      return head;
    }
    const fit =
      warmup.todayCap >= rate
        ? intl.formatMessage(messages.mailboxFits, { rate })
        : intl.formatMessage(messages.mailboxNotYet, { cap: warmup.todayCap, rate });
    return `${head} ${fit}`;
  };
  return (
    <section className={styles.card} data-testid="mailbox-ramp">
      <div className={styles.cardHead}>
        <h3 className={styles.cardTitle}>{intl.formatMessage(messages.mailboxTitle)}</h3>
        {warmup !== null && (
          <span className={clsx(styles.health, warmup.warming && styles.healthWarm)}>
            <i className={styles.healthDot} aria-hidden />
            {intl.formatMessage(warmup.warming ? messages.mailboxWarming : messages.mailboxReady)}
          </span>
        )}
      </div>
      {warmup !== null && warmup.warming && <RampBars day={warmup.day} totalDays={warmup.totalDays} />}
      <p className={`${shared.small} m-0`}>{line()}</p>
    </section>
  );
};

export const MailboxRamp = TypedMemo(MailboxRampComponent);
